import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCoursesStore } from '../store/coursesStore';
import { PlayCircle, Users, Clock, Calendar, Loader, AlertCircle } from 'lucide-react';

export default function CoursesPage() {
  const { courses, isLoading, error, fetchCourses, clearError } = useCoursesStore();

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-stone-600">جاري تحميل الكورسات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
            الكورسات المتاحة
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto">
            اختر الكورس المناسب لك وابدأ رحلة التعلم
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
              <button
                onClick={clearError}
                className="mr-auto text-red-600 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Courses Grid */}
        {courses.length === 0 && !isLoading ? (
          <div className="text-center py-16">
            <PlayCircle className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-600 mb-2">
              لا توجد كورسات متاحة حالياً
            </h3>
            <p className="text-stone-500">
              يرجى العودة لاحقاً للاطلاع على الكورسات الجديدة
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course) => (
              <div
                key={course.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                {/* Course Thumbnail */}
                <div className="relative h-48 bg-gradient-to-br from-amber-100 to-stone-100 overflow-hidden">
                  {course.thumbnail_url ? (
                    <img
                      src={course.thumbnail_url}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-16 h-16 bg-amber-200 rounded-full flex items-center justify-center">
                        <PlayCircle className="w-8 h-8 text-amber-600" />
                      </div>
                    </div>
                  )}
                  
                  {/* Price Badge */}
                  {course.price > 0 && (
                    <div className="absolute top-4 right-4 bg-amber-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {course.price} ر.س
                    </div>
                  )}
                </div>

                {/* Course Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-stone-900 mb-3 line-clamp-2">
                    {course.title}
                  </h3>
                  
                  {course.description && (
                    <p className="text-stone-600 mb-4 line-clamp-3 leading-relaxed">
                      {course.description}
                    </p>
                  )}

                  {/* Course Stats */}
                  <div className="flex items-center justify-between text-sm text-stone-500 mb-6">
                    <div className="flex items-center space-x-1 rtl:space-x-reverse">
                      <PlayCircle className="w-4 h-4" />
                      <span>{course.video_count || 0} مقطع</span>
                    </div>
                    
                    <div className="flex items-center space-x-1 rtl:space-x-reverse">
                      <Clock className="w-4 h-4" />
                      <span>{course.duration_months} شهر</span>
                    </div>
                    
                    <div className="flex items-center space-x-1 rtl:space-x-reverse">
                      <Users className="w-4 h-4" />
                      <span>{course.active_subscriptions || 0} طالب</span>
                    </div>
                  </div>

                  {/* Course Date */}
                  <div className="flex items-center space-x-2 rtl:space-x-reverse text-xs text-stone-400 mb-6">
                    <Calendar className="w-4 h-4" />
                    <span>تم النشر في {formatDate(course.created_at)}</span>
                  </div>

                  {/* Action Button */}
                  <Link
                    to={`/course/${course.id}`}
                    className="block w-full bg-amber-600 text-white text-center py-3 px-4 rounded-lg font-semibold hover:bg-amber-700 transition-colors"
                  >
                    عرض التفاصيل
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More Section - Future Enhancement */}
        {courses.length > 0 && (
          <div className="text-center mt-12">
            <p className="text-stone-500">
              عرض {courses.length} كورس من إجمالي الكورسات المتاحة
            </p>
          </div>
        )}
      </div>
    </div>
  );
}